import { ArrowUpRight, X } from "lucide-react";
import { useCallback, useState } from "react";

import {
  CaseDocument,
  DocumentEntityType,
  DocumentType,
} from "../../../api/caseDocuments";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { cn } from "../../../lib/utils";
import { fetchDocumentDownloadUrl } from "../../../thunks/caseDocument";
import { selectDocumentsByTypeAndEntityId } from "../logic/caseDocumentsSelector";
import DeleteDocumentModal from "./DeleteDocumentModal";
import UploadDocumentModal from "./uploadDocumentModal";

export interface ReportDocumentsProps {
  document_type: DocumentType;
  entityType: DocumentEntityType;
  entityId: string;
  disabledUploadButton?: boolean;
  action?: "delete" | "view";
  hideUploadButton?: boolean;
  hideTitle?: boolean;
  title?: string;
}

const ReportDocuments: React.FC<ReportDocumentsProps> = ({
  document_type,
  entityType,
  entityId,
  disabledUploadButton = false,
  action = "delete",
  hideUploadButton = false,
  hideTitle = false,
  title,
}) => {
  const documents = useAppSelector(
    entityId
      ? selectDocumentsByTypeAndEntityId(document_type, entityId)
      : () => [],
  );

  const dispatch = useAppDispatch();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<CaseDocument | null>(
    null,
  );

  const handleDeleteClick = (e: React.MouseEvent, doc: CaseDocument) => {
    e.stopPropagation();
    setSelectedDocument(doc);
    setIsDeleteModalOpen(true);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleViewDocument = useCallback(
    async (doc: CaseDocument) => {
      try {
        const { download_url } = await dispatch(
          fetchDocumentDownloadUrl({
            document_id: doc.document_id,
            params: {
              file_reference: doc.file_reference,
              mode: "VIEW",
            },
          })
        ).unwrap();

        window.open(download_url, "_blank", "noopener,noreferrer");
      } catch (error) {
        console.error("Failed to open document:", error);
      }
    },
    [dispatch]
  );

  const showUploadLink = action !== "view" && !hideUploadButton;
  const showUploadModal = isUploadModalOpen && showUploadLink;
  const showDeleteModal = isDeleteModalOpen && action === "delete";
  const showTitleText = !hideTitle;
  const isViewAction = action === "view";

  return (
    <div className={cn("flex flex-col gap-2", showTitleText && !isViewAction && "mt-4")}>
      {showTitleText && (
        <span
          className={
            isViewAction
              ? "font-sans font-medium text-xs leading-[100%] text-[#737373]"
              : "text-sm font-medium text-[#0A0A0A]"
          }
        >
          {title ?? "Documents"}
        </span>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {isViewAction && documents.length === 0 ? (
          <span className="font-sans text-xs font-normal leading-4 text-[#737373]">
            No documents attached yet
          </span>
        ) : (
          documents.map((doc) => (
            <div
              key={doc.document_id}
              className={cn(
                "flex w-[150px] px-2 py-1 justify-between items-center rounded-md border border-transparent bg-indigo-50",
                action === "view" && "cursor-pointer hover:opacity-90"
              )}
              onClick={
                action === "view"
                  ? () => {
                      void handleViewDocument(doc);
                    }
                  : undefined
              }
            >
              <span className="overflow-hidden truncate text-xs font-medium leading-4 text-gray-600 font-sans">
                {doc.file_name}
              </span>
              {action === "delete" ? (
                <button
                  type="button"
                  className="flex h-3 w-3 items-center justify-center text-gray-600 hover:text-gray-900"
                  onClick={(e) => handleDeleteClick(e, doc)}
                >
                  <X className="h-3 w-3" />
                </button>
              ) : (
                <button
                  type="button"
                  className="flex h-3 w-3 items-center justify-center text-gray-600 hover:text-gray-900"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleViewDocument(doc);
                  }}
                >
                  <ArrowUpRight className="h-3 w-3" />
                </button>
              )}
            </div>
          ))
        )}

        {showUploadLink && (
          <button
            className={cn(
              "text-xs font-medium text-indigo-500 leading-4 self-center",
              disabledUploadButton
                ? "opacity-50 cursor-not-allowed"
                : "hover:opacity-90 cursor-pointer",
            )}
            onClick={handleUploadClick}
            disabled={disabledUploadButton}
          >
            + Go to Documents to upload
          </button>
        )}
      </div>

      {showUploadModal && (
        <UploadDocumentModal
          onClose={() => setIsUploadModalOpen(false)}
          initialDocumentType={document_type}
          initialEntityType={entityType}
          initialEntityId={entityId}
          lockDocumentType={true}
          lockEntity={true}
        />
      )}

      {showDeleteModal && selectedDocument && (
        <DeleteDocumentModal
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedDocument(null);
          }}
          fileName={selectedDocument.file_name}
          selectedDocumentId={selectedDocument.document_id}
        />
      )}
    </div>
  );
};

export default ReportDocuments;
